/**
 * HomePage Component
 * Landing page for regular users with hero section and featured team
 */

import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { teamsService } from '../services/teams.service';
import { useThemeStore } from '../stores/theme.store';

/**
 * Get ISO week number for a given date
 * Used to determine Team of the Week
 */
const getISOWeek = (date: Date): number => {
  const target = new Date(date.valueOf());
  const dayNumber = (date.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNumber + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  if (target.getDay() !== 4) {
    target.setMonth(0, 1 + ((4 - target.getDay() + 7) % 7));
  }
  return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
};

export const HomePage = () => {
  const navigate = useNavigate();
  const isDark = useThemeStore((s) => s.isDark);

  // Fetch all teams for Team of the Week
  const { data: teams = [] } = useQuery({
    queryKey: ['teams'],
    queryFn: teamsService.getAll,
  });

  // Calculate Team of the Week using deterministic algorithm
  const getTeamOfTheWeek = () => {
    if (teams.length === 0) return null;

    const currentWeek = getISOWeek(new Date());
    const currentYear = new Date().getFullYear();

    // Combine year and week to create unique seed for rotation
    // This ensures teams rotate weekly and restart each year
    const seed = currentYear * 100 + currentWeek;
    const teamIndex = seed % teams.length;

    return teams[teamIndex];
  };

  const teamOfTheWeek = getTeamOfTheWeek();

  return (
    <Layout>
      <div>
        {/* Hero Section */}
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
          {/* Left: Welcome Text + Action Buttons */}
          <div className="flex-1 space-y-12">
            {/* Hero Text */}
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-heading text-center lg:text-left" style={{ textWrap: 'balance' }}>
                Discover the teammates and test your knowledge
              </h1>
            </div>

            {/* Hero Illustration — mobile only (between heading and buttons) */}
            <div className="flex justify-center lg:hidden">
              <img
                src={isDark ? '/TW hero illustration circle dark.png' : '/TW hero illustration circle.png'}
                alt="Team Wizard Hero"
                className="w-64 sm:w-80 h-64 sm:h-80 object-contain"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row flex-wrap justify-center lg:justify-start gap-6 sm:gap-9">
              {/* Search Members Button (Blue) */}
              <Button
                onClick={() => navigate('/members')}
                className="w-full sm:w-[180px] h-16 flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-600 text-white text-base rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200"
              >
                Search Members
              </Button>

              {/* Find Team Button (Green) */}
              <Button
                onClick={() => navigate('/teams')}
                className="w-full sm:w-[180px] h-16 flex items-center justify-center bg-gradient-to-br from-green-500 to-green-600 text-white text-base rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200"
              >
                Find Team
              </Button>

              {/* Play Quiz Button (Purple) */}
              <Button
                onClick={() => navigate('/quiz')}
                className="w-full sm:w-[180px] h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-purple-600 text-white text-base rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200"
              >
                Play Quiz
              </Button>
            </div>
          </div>

          {/* Right: Hero Illustration — desktop only */}
          <div className="hidden lg:block flex-shrink-0">
            <img
              src={isDark ? '/TW hero illustration circle dark.png' : '/TW hero illustration circle.png'}
              alt="Team Wizard Hero"
              className="w-96 h-96 object-contain"
            />
          </div>
        </div>

        {/* Featured Section */}
        <div className="space-y-4" style={{ marginTop: '50px' }}>
          <h2 className="text-3xl font-bold text-heading text-center sm:text-left">Featured</h2>

          {/* Team of the Week Card */}
          {teamOfTheWeek ? (
            <Link to={`/teams/${teamOfTheWeek.id}`} className="block">
              <div className="bg-surface rounded-lg border border-[var(--border-default)] shadow p-6 hover:shadow-lg hover:border-strong transition-all duration-200 cursor-pointer">
                <div className="flex items-center gap-6">
                  {/* Left: Team Week Image */}
                  <div className="flex-shrink-0">
                    <img
                      src="/team-week.png"
                      alt="Team of the Week"
                      className="w-32 h-32 object-contain"
                    />
                  </div>

                  {/* Right: Team Information */}
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-heading mb-2">
                      {teamOfTheWeek.name}
                    </h3>
                    <p className="text-meta">
                      Discover the members of {teamOfTheWeek.name}
                    </p>
                  </div>
                </div>
              </div>
            </Link>
          ) : (
            <div className="bg-surface rounded-lg border border-[var(--border-default)] shadow p-6">
              <p className="text-meta text-center">No teams available yet</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
